import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";
import { graphConfig, loginRequest } from "./msalConfig";

/**
 * Service for handling OneDrive operations using Microsoft Graph API
 */
class OneDriveService {
  /**
   * Initialize the OneDrive service with the MSAL instance
   * @param {Object} msalInstance - The MSAL instance from context
   */
  constructor(msalInstance) {
    this.msalInstance = msalInstance;
  }

  /**
   * Get active account
   * @returns {Object} Active account
   */
  getActiveAccount() {
    // First try to get the active account
    let activeAccount = this.msalInstance.getActiveAccount();

    // If no active account is found, but there are accounts available, set the first one as active
    if (!activeAccount) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
      }
    }

    return activeAccount;
  }

  /**
   * Get access token silently (no popups) for Microsoft Graph API
   * @returns {Promise<string|null>} Access token or null if it fails silently
   */
  async getTokenSilently() {
    const account = this.getActiveAccount();

    if (!account) {
      console.log('No active account for silent token acquisition');
      return null;
    }

    try {
      console.log('loginRequest', loginRequest);
      // Force token refresh to ensure we have the latest token
      const response = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
        forceRefresh: true  // Force a token refresh from the server
      });

      if (response && response.accessToken) {
        // Save the token to session storage for easier access
        sessionStorage.setItem('oneDriveToken', response.accessToken);
        return response.accessToken;
      } else {
        console.error("Acquired token but it's missing or invalid");
        return null;
      }
    } catch (error) {
      // Check if this is an interaction required error (consent needed)
      if (error instanceof InteractionRequiredAuthError) {
        console.log("Interactive consent required. Attempting to get token with popup.");
        try {
          // Try to get token with popup without explicit prompt parameter
          // Microsoft will automatically show consent if needed
          const response = await this.msalInstance.acquireTokenPopup({
            ...loginRequest,
            account: account
            // No prompt parameter - Microsoft will handle consent automatically
          });
          
          if (response && response.accessToken) {
            // Save the token to session storage for easier access
            sessionStorage.setItem('oneDriveToken', response.accessToken);
            return response.accessToken;
          }
        } catch (popupError) {
          console.error("Error during interactive consent:", popupError);
          return null;
        }
      } else {
        // If silent token acquisition fails, just return null instead of showing popup
        console.error("Silent token acquisition failed:", error);
        return null;
      }
    }
  }

  /**
   * Get access token for Microsoft Graph API, with popup if needed
   * @returns {Promise<string>} Access token
   */
  async getAccessToken() {
    const account = this.getActiveAccount();

    if (!account) {
      throw new Error("No active account! Sign in required.");
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      return response.accessToken;
    } catch (error) {
      // If silent token acquisition fails, acquire token using popup
      if (error instanceof InteractionRequiredAuthError) {
        try {
          // Request a new token without specifying prompt parameter
          // Microsoft will automatically show consent if needed
          const response = await this.msalInstance.acquireTokenPopup({
            ...loginRequest,
            account: account
            // No prompt parameter - Microsoft will handle consent automatically
          });
          
          // Save the token to session storage for easier access
          if (response && response.accessToken) {
            sessionStorage.setItem('oneDriveToken', response.accessToken);
          }
          
          return response.accessToken;
        } catch (err) {
          console.error("Error acquiring token with consent:", err);
          throw err;
        }
      } else {
        console.error("Error acquiring token silently:", error);
        throw error;
      }
    }
  }

  /**
   * Call Microsoft Graph API
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} [data] - Request body for POST/PUT
   * @returns {Promise<Object>} API response
   */
  async callMsGraph(endpoint, method = "GET", data = null) {
    const token = await this.getAccessToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios({
        method: method,
        url: endpoint,
        headers: headers,
        data: data,
      });
      return response.data;
    } catch (error) {
      console.error("Error calling MS Graph API:", error);
      throw error;
    }
  }

  /**
   * Get user's OneDrive items (files and folders)
   * @param {string} [folderId] - Optional folder ID to browse
   * @returns {Promise<Object>} Drive items
   */
  async getDriveItems(folderId = null) {
    let endpoint = `${graphConfig.graphDriveEndpoint}/root/children`;

    if (folderId) {
      endpoint = `${graphConfig.graphDriveEndpoint}/items/${folderId}/children`;
    }

    return this.callMsGraph(endpoint);
  }

  /**
   * Get download URL for a OneDrive file
   * @param {string} fileId - OneDrive file ID
   * @returns {Promise<string>} Download URL
   */
  async getDownloadUrl(fileId) {
    try {
      // Try to get token from session storage first for better performance
      let token = sessionStorage.getItem('oneDriveToken');

      if (!token) {
        console.log('No token in session storage, acquiring new token');
        token = await this.getAccessToken();
      }

      // Instead of directly downloading the content, get the download URL
      const endpoint = `${graphConfig.graphDriveEndpoint}/items/${fileId}`;

      console.log(`Getting download URL for file ${fileId}...`);

      const response = await axios({
        method: "GET",
        url: endpoint,
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data && response.data['@microsoft.graph.downloadUrl']) {
        return response.data['@microsoft.graph.downloadUrl'];
      } else {
        // If download URL isn't available, fall back to content endpoint
        return `${graphConfig.graphDriveEndpoint}/items/${fileId}/content`;
      }
    } catch (error) {
      // Check if it's an auth error
      if (error.response && error.response.status === 401) {
        console.log('Token expired, trying to get a new one...');
        try {
          // Try to get a fresh token
          const newToken = await this.getAccessToken();

          // Retry with the new token
          const endpoint = `${graphConfig.graphDriveEndpoint}/items/${fileId}`;
          const response = await axios({
            method: "GET",
            url: endpoint,
            headers: {
              Authorization: `Bearer ${newToken}`,
            }
          });

          if (response.data && response.data['@microsoft.graph.downloadUrl']) {
            return response.data['@microsoft.graph.downloadUrl'];
          } else {
            return `${graphConfig.graphDriveEndpoint}/items/${fileId}/content`;
          }
        } catch (retryError) {
          console.error("Error retrying with new token:", retryError);
          throw new Error('Authorization failed. Please reconnect to OneDrive.');
        }
      }

      console.error("Error getting download URL:", error);
      throw error;
    }
  }

  /**
   * Download a file from OneDrive
   * @param {string} fileId - OneDrive file ID
   * @returns {Promise<Blob>} File blob
   */
  async downloadFile(fileId) {
    try {
      // Try to get token from session storage first for better performance
      let token = sessionStorage.getItem('oneDriveToken');

      if (!token) {
        console.log('No token in session storage, acquiring new token');
        token = await this.getAccessToken();
      }

      // Get the download URL - this allows more efficient downloads with progress tracking
      const downloadUrl = await this.getDownloadUrl(fileId);

      console.log(`Downloading file ${fileId} from OneDrive...`);

      const response = await axios({
        method: "GET",
        url: downloadUrl,
        headers: {
          // If using the Microsoft Graph URL, we need the auth token
          // If using the direct download URL, we don't need the token
          ...(downloadUrl.includes('graph.microsoft.com') ? { Authorization: `Bearer ${token}` } : {})
        },
        responseType: "blob",
        timeout: 180000 // 3 minute timeout for larger files
      });

      if (!response.data) {
        throw new Error('Downloaded file is empty');
      }

      console.log(`Successfully downloaded file ${fileId} (${response.data.size} bytes)`);
      return response.data;
    } catch (error) {
      // Check if it's an auth error
      if (error.response && error.response.status === 401) {
        console.log('Token expired, trying to get a new one...');
        try {
          // Try to get a fresh token
          const newToken = await this.getAccessToken();
          
          // Get a fresh download URL
          const downloadUrl = await this.getDownloadUrl(fileId);

          // Retry the download with the new token
          const response = await axios({
            method: "GET",
            url: downloadUrl,
            headers: {
              ...(downloadUrl.includes('graph.microsoft.com') ? { Authorization: `Bearer ${newToken}` } : {})
            },
            responseType: "blob",
            timeout: 180000 // 3 minute timeout for larger files
          });

          return response.data;
        } catch (retryError) {
          console.error("Error retrying download with new token:", retryError);
          throw new Error('Authorization failed. Please reconnect to OneDrive.');
        }
      }

      console.error("Error downloading file:", error);
      throw error;
    }
  }

  /**
   * Create a OneDrive picker
   * @param {Function} onFileSelect - Callback when files are selected
   * @param {Array} acceptedFileTypes - Array of accepted file types (e.g., ["csv", "mp4", "mov"])
   */
  async openOneDrivePicker(onFileSelect, acceptedFileTypes = []) {
    try {
      // Get list of drive items
      const driveItems = await this.getDriveItems();

      // Filter by file types if provided
      let filteredItems = driveItems.value;

      if (acceptedFileTypes.length > 0) {
        filteredItems = filteredItems.filter(item => {
          if (item.file) {
            const extension = item.name.split('.').pop().toLowerCase();
            return acceptedFileTypes.includes(extension);
          }
          return false;
        });
      }

      // Process the selected files (similar to Google Drive picker)
      const processedFiles = filteredItems.map(item => ({
        id: item.id,
        name: item.name,
        mimeType: item.file.mimeType,
        size: item.size,
        webUrl: item.webUrl,
        downloadUrl: item?.['@microsoft.graph.downloadUrl'],
        thumbnailUrl: item.thumbnailUrl,
        createdDateTime: item.createdDateTime,
        lastModifiedDateTime: item.lastModifiedDateTime,
      }));

      // Call the callback with the selected files
      onFileSelect({
        action: "picked",
        docs: processedFiles,
      });

      return processedFiles;
    } catch (error) {
      console.error("Error opening OneDrive picker:", error);
      throw error;
    }
  }

  /**
   * Convert OneDrive file to browser File object
   * @param {Object} oneDriveFile - OneDrive file object
   * @returns {Promise<File>} Browser File object
   */
  async convertToBrowserFile(oneDriveFile) {
    try {
      console.log('Converting OneDrive file to browser file:', oneDriveFile.name);

      // Handle case where we don't have a valid token
      if (!sessionStorage.getItem('oneDriveToken')) {
        // Try to get a new token silently
        const token = await this.getTokenSilently();
        if (!token) {
          throw new Error('No valid token available for OneDrive. Please reconnect.');
        }
      }

      // Download the file content
      const blob = await this.downloadFile(oneDriveFile.id);

      if (!blob) {
        throw new Error(`Failed to download ${oneDriveFile.name} from OneDrive`);
      }

      // Create a File object with the actual content
      return new File([blob], oneDriveFile.name, {
        type: oneDriveFile.mimeType || this.getMimeTypeFromName(oneDriveFile.name),
        lastModified: new Date(oneDriveFile.lastModifiedDateTime || Date.now()).getTime(),
      });
    } catch (error) {
      console.error("Error converting to browser file:", error);
      throw error;
    }
  }
  /**
   * Get MIME type from file name
   * @param {string} fileName - File name with extension
   * @returns {string} MIME type
   */
  getMimeTypeFromName(fileName) {
    const extension = fileName.split('.').pop().toLowerCase();
    const mimeTypes = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',
      'mp4': 'video/mp4',
      'mov': 'video/quicktime',
      'avi': 'video/x-msvideo',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'mkv': 'video/x-matroska',
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'txt': 'text/plain',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'text/javascript',
      'json': 'application/json',
      'xml': 'application/xml',
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      'tar': 'application/x-tar',
      'csv': 'text/csv'
    };

    return mimeTypes[extension] || 'application/octet-stream';
  }
}

export default OneDriveService;